import React from 'react';
import ImageGallery from 'react-image-gallery';

import { getReferenceUrl, convertRToSuperscript } from './utils/utils';
import GenericLink from './components/GenericLink';
import Video from './components/Video';
import FileRow from './components/FileRow';
import Quote from './components/Quote';
import InfoBox from './components/InfoBox';
import { RawPortableText } from './types';

import * as styles from './serializers.module.scss';
import Card from './components/Card';

interface GenericLinkSerializerProps {
  mark: { url: string };
  children: React.ReactNode;
}
export const getGenericLinkSerializer = ({
  openInNewTab = false,
} = {}): React.FC<GenericLinkSerializerProps> => {
  const GenericLinkSerializer = (props: GenericLinkSerializerProps): React.ReactElement => {
    return (
      <GenericLink openInNewTab={openInNewTab} to={props.mark.url}>
        {props.children}
      </GenericLink>
    );
  };
  return GenericLinkSerializer;
};

interface ReferenceLinkSerializerProps {
  mark: {
    reference: {
      slug: { current: string };
      internal: {
        type:
          | 'SanityProduct'
          | 'SanityPage'
          | 'SanityLegalDocumentation'
          | 'SanityCrop'
          | 'SanityNewsArticle'
          | 'SanityResourceArticle';
      };
    };
  };
  children: React.ReactNode;
}
export const getReferenceLinkSerializer = ({
  openInNewTab = false,
} = {}): React.FC<ReferenceLinkSerializerProps> => {
  const ReferenceLinkSerializer = (props: ReferenceLinkSerializerProps): React.ReactElement => {
    return (
      <GenericLink
        openInNewTab={openInNewTab}
        to={getReferenceUrl(props.mark.reference.internal.type, props.mark.reference.slug.current)}
      >
        {props.children}
      </GenericLink>
    );
  };

  return ReferenceLinkSerializer;
};

const ColorAnnotationSerializer = (props: {
  mark: {
    color: string;
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return <span style={{ color: props.mark.color }}>{props.children}</span>;
};

const ResourceFileSerializer = (props: {
  node: {
    file: {
      asset: {
        size: number;
        url: string;
      };
    };
    title: string;
  };
}): React.ReactElement => {
  return (
    <FileRow
      title={props.node.title}
      url={props.node.file.asset.url}
      size={props.node.file.asset.size}
    ></FileRow>
  );
};

const QuoteSerializer = (props: {
  node: {
    text: string;
    author: string;
  };
}): React.ReactElement => {
  return (
    <div className={styles.quote}>
      <Quote text={props.node.text} author={props.node.author}></Quote>
    </div>
  );
};

const InfoBoxSerializer = (props: {
  node: {
    backgroundColor: string;
    title: string;
    content: RawPortableText;
  } & (
    | {
        readMoreUrl?: {
          url: string;
        };
        readMoreReference?: never;
      }
    | {
        readMoreUrl?: never;
        readMoreReference?: {
          internal: {
            type: 'SanityProduct' | 'SanityCrop' | 'SanityNewsArticle' | 'SanityResourceArticle';
          };
          slug: {
            current: string;
          };
        };
      }
  );
}): React.ReactElement => {
  const readMoreProps = props.node.readMoreUrl
    ? {
        readMoreUrl: props.node.readMoreUrl.url,
      }
    : {
        readMoreReference: props.node.readMoreReference,
      };
  return (
    <div className={styles.infoBox}>
      <InfoBox
        backgroundColor={props.node.backgroundColor}
        title={props.node.title}
        content={props.node.content}
        {...readMoreProps}
      ></InfoBox>
    </div>
  );
};

const CardGridSerializer = (props: {
  node: {
    cards: Array<{
      title: string;
      image: {
        asset: {
          url: string;
        };
      };
      url?: string;
      linkReference?: {
        internal: {
          type:
            | 'SanityProduct'
            | 'SanityCrop'
            | 'SanityNewsArticle'
            | 'SanityResourceArticle'
            | 'SanityPage';
        };
        slug: {
          current: string;
        };
      };
    }>;
  };
}): React.ReactElement => {
  return (
    <div className={styles.cardGrid}>
      {props.node.cards.map((card, i) => (
        <div className={styles.card} key={i}>
          <Card
            imageUrl={card.image.asset.url}
            title={card.title}
            linkUrl={
              card.linkReference
                ? getReferenceUrl(card.linkReference.internal.type, card.linkReference.slug.current)
                : card.url
            }
          />
        </div>
      ))}
    </div>
  );
};

const YoutubeSerializer = (props: { node: { url: string } }): React.ReactElement => {
  return (
    <div className={styles.video}>
      <Video url={props.node.url}></Video>
    </div>
  );
};

const EmbeddedImageSerializer = (props: {
  node: {
    image: {
      asset: {
        originalFilename: string;
        url: string;
      };
    };
    imageWidth: number;
  };
}): React.ReactElement => {
  return (
    <div className={styles.embbededImageWrapper}>
      <img
        src={props.node.image.asset.url + ('?w=' + (props.node.imageWidth || '800') + '&fit=max')}
        alt={props.node.image.asset.originalFilename}
      />
    </div>
  );
};

const ImageGallerySerializer = (props: {
  node: {
    images: Array<{
      asset: {
        originalFilename: string;
        url: string;
      };
    }>;
  };
}): React.ReactElement => {
  return (
    <ImageGallery
      items={props.node.images.map(image => ({
        original: image.asset.url + '?w=1000&h=600&fit=crop',
        fullscreen: image.asset.url + '?w=1000&h=600&fit=crop',
        thumbnail: image.asset.url + '?w=100&h=67&fit=crop',
        originalAlt: image.asset.originalFilename,
      }))}
      additionalClass={styles.imageGallery}
      showBullets
    />
  );
};

const TextSerializer = (props: { children: string }): React.ReactElement => {
  return <>{convertRToSuperscript(props.children)}</>;
};

const serializers = {
  marks: {
    genericLink: getGenericLinkSerializer(),
    productLink: getReferenceLinkSerializer(),
    pageLink: getReferenceLinkSerializer(),
    legalDocumentationLink: getReferenceLinkSerializer(),
    cropLink: getReferenceLinkSerializer(),
    newsArticleLink: getReferenceLinkSerializer(),
    resourceArticleLink: getReferenceLinkSerializer(),
    colorAnnotation: ColorAnnotationSerializer,
  },
  types: {
    resourceFile: ResourceFileSerializer,
    resourceFileReference: ResourceFileSerializer,
    quote: QuoteSerializer,
    infoBox: InfoBoxSerializer,
    cardGrid: CardGridSerializer,
    youtube: YoutubeSerializer,
    embeddedImage: EmbeddedImageSerializer,
    imageGallery: ImageGallerySerializer,
  },
  text: TextSerializer,
};

export default serializers;
